import React, { useMemo, useState, useEffect, useCallback, useRef, memo } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
  Grid,
  Autocomplete,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import { useGetLakehousesByCustomerAndClientQuery, useGetDataTablesQuery } from '../../../api/lakehouseApiSlice';
import { useDataAgentSourceManagement } from './AddEditDataAgentSourceManagement';
import { useUpdateDataAgentSourceMutation, useGetDataAgentSourcesQuery } from '../../../api/dataAgentApiSlice';
import {
  useGetDataConnectionsByClientIdQuery,
  useGetDataConnectionByIdQuery,
} from '../../../api/dataConnectionApiSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { HelpCallout } from '../../../components/HelpCallout';
import { showToaster } from '../../../utils/globalImports';

interface ColumnMetadata {
  columnName: string;
  description: string;
  synonymsNotes: string;
  sourceInstructions: string;
}

interface AddEditDataAgentSourceUIFormProps {
  onCancel: () => void;
  initialData?: any;
  dataAgentId: string;
  existingSourceType?: string | null;
}

// Helper function for error messages
const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.filter((e) => typeof e === 'string').join(', ');
  return '';
};

// ULTRA OPTIMIZED: Isolated TextField with memo and local state
interface IsolatedTextFieldProps {
  initialValue: string;
  onUpdate: (value: string) => void;
  placeholder: string;
  delay?: number;
}

const IsolatedTextField = memo(({ initialValue, onUpdate, placeholder, delay = 500 }: IsolatedTextFieldProps) => {
  const [value, setValue] = useState(initialValue);
  const timeoutRef = useRef<number | undefined>(undefined);
  const isMountedRef = useRef(true);

  // Update local state if initial value changes externally
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue); // Update immediately for responsive UI

      // Clear existing timeout
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      // Debounce the actual update
      timeoutRef.current = window.setTimeout(() => {
        if (isMountedRef.current) {
          onUpdate(newValue);
        }
      }, delay);
    },
    [onUpdate, delay],
  );

  return (
    <TextField
      fullWidth
      multiline
      minRows={2}
      maxRows={6}
      variant="outlined"
      size="small"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      sx={{
        '& .MuiInputBase-root': {
          borderRadius: 1,
        },
      }}
    />
  );
});

IsolatedTextField.displayName = 'IsolatedTextField';

// ULTRA OPTIMIZED: Memoized table row to prevent unnecessary re-renders
interface ColumnRowProps {
  column: ColumnMetadata;
  originalIndex: number;
  onUpdateDescription: (index: number, value: string) => void;
  onUpdateSynonyms: (index: number, value: string) => void;
  descriptionPlaceholder: string;
  synonymsPlaceholder: string;
}

const ColumnRow = memo(
  ({
    column,
    originalIndex,
    onUpdateDescription,
    onUpdateSynonyms,
    descriptionPlaceholder,
    synonymsPlaceholder,
  }: ColumnRowProps) => {
    const handleDescriptionUpdate = useCallback(
      (value: string) => {
        onUpdateDescription(originalIndex, value);
      },
      [originalIndex, onUpdateDescription],
    );

    const handleSynonymsUpdate = useCallback(
      (value: string) => {
        onUpdateSynonyms(originalIndex, value);
      },
      [originalIndex, onUpdateSynonyms],
    );

    return (
      <TableRow
        hover
        sx={{
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <TableCell
          sx={{
            verticalAlign: 'top',
            py: 1.5,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            bgcolor: 'grey.50',
            borderRight: '1px solid',
            borderColor: 'grey.200',
            width: '140px',
            maxWidth: '140px',
            overflow: 'hidden',
          }}
        >
          <Tooltip title={column.columnName} placement="top" arrow enterDelay={300}>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                fontWeight: 500,
                display: 'block',
                maxWidth: '100%',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {column.columnName}
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell
          sx={{
            p: 1.5,
            verticalAlign: 'top',
            width: 'calc(60% - 70px)',
          }}
        >
          <IsolatedTextField
            initialValue={column.description}
            onUpdate={handleDescriptionUpdate}
            placeholder={descriptionPlaceholder}
            delay={500}
          />
        </TableCell>
        <TableCell
          sx={{
            p: 1.5,
            verticalAlign: 'top',
            width: 'calc(40% - 70px)',
          }}
        >
          <IsolatedTextField
            initialValue={column.synonymsNotes}
            onUpdate={handleSynonymsUpdate}
            placeholder={synonymsPlaceholder}
            delay={500}
          />
        </TableCell>
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders
    return (
      prevProps.column.columnName === nextProps.column.columnName &&
      prevProps.column.description === nextProps.column.description &&
      prevProps.column.synonymsNotes === nextProps.column.synonymsNotes &&
      prevProps.originalIndex === nextProps.originalIndex
    );
  },
);

ColumnRow.displayName = 'ColumnRow';

const AddEditDataAgentSourceUIForm: React.FC<AddEditDataAgentSourceUIFormProps> = ({
  onCancel,
  initialData,
  dataAgentId,
  existingSourceType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedCustomerId: customerId, selectedClientId: clientId } = useSelector(
    (state: RootState) => state.vaultilake,
  );
  const { validationSchema, handleSubmit } = useDataAgentSourceManagement();
  const [updateDataAgentSource] = useUpdateDataAgentSourceMutation();

  const isEditMode = !!initialData?.DataAgentSourceId;

  // State for tracking selected data table
  const [checkDataTable, setCheckDataTable] = useState<string>('');

  // State for connector ID in edit mode
  const [connectorForEditing, setConnectorForEditing] = useState<string>('');

  // State for toggle status
  const [isSourceActive, setIsSourceActive] = useState<boolean>(true);

  // Adding state for search columns
  const [searchQuery, setSearchQuery] = useState<string>('');

  // NEW: State for column metadata loading
  const [isLoadingColumnMetadata, setIsLoadingColumnMetadata] = useState<boolean>(false);
  const [currentLoadingFileId, setCurrentLoadingFileId] = useState<string>('');

  // NEW: Abort controller ref for canceling operations
  const abortControllerRef = useRef<AbortController | null>(null);

  const [allowReinitialize, setAllowReinitialize] = useState(true);

  // Fetch data connections
  const {
    data: dataConnectionsResponse,
    isLoading: isLoadingDataConnections,
    isError: isErrorDataConnections,
  } = useGetDataConnectionsByClientIdQuery(clientId ? clientId : skipToken, {
    skip: !clientId,
  });

  const dataConnections = useMemo(() => {
    if (Array.isArray(dataConnectionsResponse)) {
      return dataConnectionsResponse;
    }
    if (dataConnectionsResponse?.connections) {
      return dataConnectionsResponse.connections;
    }
    return [];
  }, [dataConnectionsResponse]);

  // Fetch ALL data agent sources for checking if table exists
  const { data: dataAgentSourcesResponse, isLoading: isLoadingDataAgentSources } = useGetDataAgentSourcesQuery(
    {
      data_agent_id: dataAgentId,
      customer_id: customerId,
      client_id: clientId,
    },
    {
      skip: !dataAgentId || !customerId || !clientId,
    },
  );

  // Extract selected table IDs for Lakehouse sources
  const existingLakehouseTableIds = useMemo(() => {
    if (!dataAgentSourcesResponse?.data_agent_sources) {
      return [];
    }
    return dataAgentSourcesResponse.data_agent_sources
      .filter((source: any) => source.SourceType === 'Lakehouse')
      .map((source: any) => ({
        tableId: source.SelectedTables,
        isActive: source.IsActive,
      }));
  }, [dataAgentSourcesResponse]);

  // Consolidate connector extraction into single useEffect
  useEffect(() => {
    if (isEditMode && initialData?.SourceType === 'DataConnection' && dataAgentSourcesResponse?.data_agent_sources) {
      const currentSource = dataAgentSourcesResponse.data_agent_sources.find(
        (source: any) => source.DataAgentSourceId === initialData.DataAgentSourceId,
      );
      if (currentSource?.source_id) {
        setConnectorForEditing(currentSource.source_id);
      }
    }
  }, [isEditMode, initialData?.DataAgentSourceId, initialData?.SourceType, dataAgentSourcesResponse]);

  // Fetch specific data connection details in edit mode
  const { data: connectionData, isLoading: isConnectionLoading } = useGetDataConnectionByIdQuery(
    isEditMode && connectorForEditing ? connectorForEditing : skipToken,
  );

  // Fetch lakehouses
  const {
    data: lakehousesResponse,
    isLoading: isLoadingLakehouses,
    isError: isErrorLakehouses,
  } = useGetLakehousesByCustomerAndClientQuery(
    customerId && clientId
      ? {
          customerId,
          clientId,
          includeLayers: true,
        }
      : skipToken,
  );
  const lakehouses = useMemo(() => {
    if (!lakehousesResponse) return [];
    if (Array.isArray(lakehousesResponse)) return lakehousesResponse;
    if ((lakehousesResponse as any)?.lakehouses) return (lakehousesResponse as any).lakehouses;
    return [];
  }, [lakehousesResponse]);

  const getGoldLayerId = useCallback(
    (lakehouseId: string) => {
      if (!lakehouses || !lakehouseId) return null;
      const lakehouse = lakehouses.find((lh: any) => lh.lakehouse_id === lakehouseId);
      if (!lakehouse?.lakehouse_layers) return null;
      const goldLayer = lakehouse.lakehouse_layers.find((layer: any) => layer.medallion_layer === 'Gold');
      return goldLayer?.lakehouse_layer_id || null;
    },
    [lakehouses],
  );

  // Extract lakehouse ID from initialData
  const initialLakehouseId = useMemo(() => {
    if (isEditMode && initialData?.Lakehouse) {
      return initialData.Lakehouse.LakehouseId;
    }
    return '';
  }, [isEditMode, initialData?.Lakehouse]);

  const [selectedLakehouseId, setSelectedLakehouseId] = useState<string>(isEditMode ? initialLakehouseId : '');

  // Consolidate lakehouse ID update
  useEffect(() => {
    if (isEditMode && initialLakehouseId && selectedLakehouseId !== initialLakehouseId) {
      setSelectedLakehouseId(initialLakehouseId);
    }
  }, [isEditMode, initialLakehouseId]);

  useEffect(() => {
    if (!isEditMode) {
      setAllowReinitialize(false);
    }
  }, [isEditMode]);

  const currentGoldLayerId = useMemo(() => getGoldLayerId(selectedLakehouseId), [selectedLakehouseId, getGoldLayerId]);

  const {
    data: filesData,
    isLoading: isLoadingFiles,
    isError: isErrorFiles,
  } = useGetDataTablesQuery(currentGoldLayerId as string, {
    skip: !currentGoldLayerId,
  });

  const dataTables = useMemo(() => {
    if (!filesData) return [];
    if (Array.isArray(filesData)) return filesData;
    const data = filesData as any;
    if (data.data_tables && Array.isArray(data.data_tables)) return data.data_tables;
    if (data.tables && Array.isArray(data.tables)) return data.tables;
    return [];
  }, [filesData]);

  // Memoize parse functions
  const parseColumnMetadata = useCallback((source: any): ColumnMetadata[] => {
    if (!source) return [];

    // Try column_descriptions first
    if (source.column_descriptions) {
      try {
        const columnData = JSON.parse(source.column_descriptions);
        if (Array.isArray(columnData)) return columnData;
      } catch (error) {
        console.error('Error parsing column_descriptions:', error);
      }
    }

    // Try ColumnDescriptions
    if ((source as any).ColumnDescriptions) {
      try {
        const columnData = JSON.parse((source as any).ColumnDescriptions);
        if (Array.isArray(columnData)) return columnData;
      } catch (error) {
        console.error('Error parsing ColumnDescriptions:', error);
      }
    }

    // Try SourceInstructions fallback
    if (source?.SourceInstructions) {
      try {
        const instructions = JSON.parse(source.SourceInstructions);
        if (instructions.columnMetadata && Array.isArray(instructions.columnMetadata)) {
          return instructions.columnMetadata;
        }
      } catch (error) {
        console.error('Error parsing SourceInstructions:', error);
      }
    }

    return [];
  }, []);

  const parseSourceInstructions = useCallback((source: any): string => {
    if (!source?.SourceInstructions) return '';
    try {
      const instructions = JSON.parse(source.SourceInstructions);
      return instructions.sourceInstructions || '';
    } catch (error) {
      console.error('Error parsing source instructions:', error);
      return source.SourceInstructions;
    }
  }, []);

  // Determine initial fileId from initialData
  const initialFileId = useMemo(() => {
    if (isEditMode && initialData?.DataTableId) {
      return initialData.DataTableId;
    }
    return '';
  }, [isEditMode, initialData?.DataTableId]);

  // Determine initial dataConnectionId
  const initialDataConnectionId = useMemo(() => {
    if (isEditMode && initialData?.SourceType === 'DataConnection') {
      if (connectionData?.data_connection_id) {
        return connectionData.data_connection_id;
      }
      if (initialData?.DataConnectionId) {
        return initialData.DataConnectionId;
      }
    }
    return '';
  }, [isEditMode, initialData?.SourceType, initialData?.DataConnectionId, connectionData?.data_connection_id]);

  // Initial column metadata from stored data
  const initialColumnMetadata = useMemo(() => {
    if (isEditMode && initialData) {
      return parseColumnMetadata(initialData);
    }
    return [];
  }, [isEditMode, initialData, parseColumnMetadata]);

  // Helper: Find existing source by table/connection ID
  const findExistingSourceByIdentifier = useCallback(
    (sourceType: string, tableId?: string, connectionId?: string): any | null => {
      if (!dataAgentSourcesResponse?.data_agent_sources) return null;

      return dataAgentSourcesResponse.data_agent_sources.find((source: any) => {
        if (sourceType === 'Lakehouse' && tableId) {
          // Normalize both IDs to uppercase for comparison
          const normalizedTableId = tableId.toUpperCase();
          const normalizedSourceTableId = (
            source.SelectedTables ||
            source.DataTableId ||
            source.data_table_id ||
            source.selectedtables ||
            source.selected_tables ||
            ''
          ).toUpperCase();

          const matches = source.SourceType === 'Lakehouse' && normalizedSourceTableId === normalizedTableId;
          return matches;
        }

        if (sourceType === 'DataConnection' && connectionId) {
          const normalizedConnectionId = connectionId.toUpperCase();
          const normalizedSourceConnectionId = (
            source.source_id ||
            source.SourceId ||
            source.data_connection_id ||
            source.DataConnectionId ||
            ''
          ).toUpperCase();

          return source.SourceType === 'DataConnection' && normalizedSourceConnectionId === normalizedConnectionId;
        }
        return false;
      });
    },
    [dataAgentSourcesResponse],
  );

  // Extract column descriptions from existing sources
  const getColumnDescriptionsFromExistingSources = useCallback(
    (dataTableId: string): ColumnMetadata[] => {
      if (!dataAgentSourcesResponse?.data_agent_sources || !dataTableId) return [];

      const matchingSource = dataAgentSourcesResponse.data_agent_sources.find(
        (source: any) => source.SourceType === 'Lakehouse' && source.SelectedTables === dataTableId,
      );

      if (matchingSource) {
        return parseColumnMetadata(matchingSource);
      }
      return [];
    },
    [dataAgentSourcesResponse, parseColumnMetadata],
  );

  const initialValues = {
    dataAgentId,
    customerId: customerId || '',
    clientId: clientId || '',
    sourceType: isEditMode ? initialData?.SourceType : '',
    lakehouseId: isEditMode ? initialLakehouseId : '',
    fileId: isEditMode ? initialFileId : '',
    dataConnectionId: isEditMode ? initialDataConnectionId : '',
    sourceInstructions: isEditMode ? parseSourceInstructions(initialData) : '',
    columnMetadata: isEditMode ? initialColumnMetadata : [],
  };

  const [formKey, setFormKey] = useState(0);

  // Only reset form key when actually entering edit mode with new data
  const prevInitialDataRef = useRef<any>(null);
  useEffect(() => {
    if (isEditMode && initialData && initialData !== prevInitialDataRef.current) {
      setFormKey((prev) => prev + 1);
      prevInitialDataRef.current = initialData;
    }
  }, [isEditMode, initialData]);

  const handleFormSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      let shouldUpdate = false;
      let existingSourceId = null;

      if (isEditMode && initialData?.DataAgentSourceId) {
        // Check if the selected file/connection has an existing source in the DB
        if (values.sourceType === 'Lakehouse' && values.fileId) {
          const existingSource = findExistingSourceByIdentifier('Lakehouse', values.fileId);
          if (existingSource) {
            existingSourceId = existingSource.DataAgentSourceId;
          } else {
            existingSourceId = null; // File doesn't exist in DB, send null
          }
        } else if (values.sourceType === 'DataConnection' && values.dataConnectionId) {
          const existingSource = findExistingSourceByIdentifier('DataConnection', undefined, values.dataConnectionId);
          if (existingSource) {
            existingSourceId = existingSource.DataAgentSourceId;
          } else {
            existingSourceId = null; // Connection doesn't exist in DB, send null
          }
        }
        shouldUpdate = true; // Always use update mutation in edit mode
      }

      if (shouldUpdate) {
        const updatePayload: any = {
          data_agent_source_id: existingSourceId, // Will be null if file doesn't exist in DB
          data_agent_id: dataAgentId,
          customer_id: customerId || '',
          client_id: clientId || '',
          source_type: values.sourceType,
          is_active: isSourceActive,
        };

        if (values.sourceType === 'Lakehouse') {
          updatePayload.lakehouse_id = values.lakehouseId;
          updatePayload.data_table_id = values.fileId;
          updatePayload.data_connection_id = null;
          updatePayload.source_instructions = JSON.stringify({
            sourceInstructions: values.sourceInstructions,
            columnMetadata: values.columnMetadata,
          });
        } else if (values.sourceType === 'DataConnection') {
          updatePayload.data_connection_id = values.dataConnectionId;
          updatePayload.lakehouse_id = null;
          updatePayload.data_table_id = null;
          if (values.sourceInstructions && values.sourceInstructions.trim()) {
            updatePayload.source_instructions = JSON.stringify({
              sourceInstructions: values.sourceInstructions,
            });
          } else {
            updatePayload.source_instructions = null;
          }
        }
        console.log('Update Payload:', updatePayload);
        await updateDataAgentSource(updatePayload).unwrap();
        dispatch(
          showToaster({
            message: t('dataAgents.apiMessages.dataAgentSourceUpdatedSuccess'),
            type: 'success',
          }),
        );
      } else {
        const createValues = {
          ...values,
          isActive: isSourceActive,
        };
        const result = await handleSubmit(createValues);
        if (!result.success) {
          setSubmitting(false);
          return;
        }
      }
      onCancel();
    } catch (error) {
      console.error('Error saving data agent source:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save source'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = useCallback(() => {
    setIsSourceActive((prev) => !prev);
  }, []);

  // Prepare source type options
  const sourceTypeOptions = [
    {
      value: 'Lakehouse',
      label: t('dataAgents.source.lakehouse'),
      // Disable Lakehouse if a DataConnection is already configured (and not editing)
      disabled: !isEditMode && existingSourceType === 'DataConnection',
    },
    {
      value: 'DataConnection',
      label: t('dataAgents.source.dataConnection'),
      // Disable DataConnection if a Lakehouse is already configured (and not editing)
      disabled: !isEditMode && existingSourceType === 'Lakehouse',
    },
  ];

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize={allowReinitialize}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, setFieldValue, isSubmitting, touched, errors, handleBlur }) => {
        const goldLayerId = getGoldLayerId(values.lakehouseId);
        const tables = goldLayerId ? dataTables : [];
        const selectedTable =
          values.fileId && tables.length > 0 ? tables.find((t: any) => t.data_table_id === values.fileId) : null;

        const filteredColumnMetadata = useMemo(() => {
          if (!searchQuery.trim()) return values.columnMetadata;
          const query = searchQuery.toLowerCase();
          return values.columnMetadata.filter(
            (column: ColumnMetadata) =>
              column.columnName.toLowerCase().includes(query) ||
              column.description?.toLowerCase().includes(query) ||
              column.synonymsNotes?.toLowerCase().includes(query),
          );
        }, [searchQuery, values.columnMetadata]);

        const configuredCount = useMemo(() => {
          return values.columnMetadata.filter(
            (col: ColumnMetadata) => col.description?.trim() || col.synonymsNotes?.trim(),
          ).length;
        }, [values.columnMetadata]);

        // Use ref to track if we've already populated for this fileId
        const populatedFileIdRef = useRef<string>('');

        // UPDATED: Column metadata loading with abort capability
        useEffect(() => {
          // Skip if not Lakehouse or no fileId
          if (values.sourceType !== 'Lakehouse' || !values.fileId) {
            setIsLoadingColumnMetadata(false);
            setCurrentLoadingFileId('');
            return;
          }

          // If a different file is being loaded, abort previous operation
          if (currentLoadingFileId && currentLoadingFileId !== values.fileId) {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
          }

          // Skip if we've already populated this fileId (prevents re-render on typing)
          if (populatedFileIdRef.current === values.fileId) {
            setIsLoadingColumnMetadata(false);
            return;
          }

          // Create new abort controller for this operation
          abortControllerRef.current = new AbortController();
          const signal = abortControllerRef.current.signal;

          // Start loading
          setIsLoadingColumnMetadata(true);
          setCurrentLoadingFileId(values.fileId);

          // Simulate async operation with Promise
          const loadColumnMetadata = async () => {
            try {
              // Check if aborted
              if (signal.aborted) return;

              // Check existing sources for current fileId
              const existingColumns = getColumnDescriptionsFromExistingSources(values.fileId);

              //  Get the existing source to extract source instructions
              const existingSource =
                isEditMode && initialData?.DataAgentSourceId
                  ? initialData?.DataTableId === values.fileId
                    ? initialData
                    : null
                  : dataAgentSourcesResponse?.data_agent_sources?.find(
                      (source: any) =>
                        source.SourceType === 'Lakehouse' &&
                        (
                          source.SelectedTables ||
                          source.DataTableId ||
                          source.datatableid ||
                          source.selectedtables
                        )?.toUpperCase() === values.fileId?.toUpperCase(),
                    );

              if (signal.aborted) return;

              if (existingColumns.length > 0) {
                setFieldValue('columnMetadata', existingColumns, false);

                //  Populate source instructions from existing source
                if (existingSource) {
                  const sourceInstructions = parseSourceInstructions(existingSource);
                  if (sourceInstructions) {
                    setFieldValue('sourceInstructions', sourceInstructions, false);
                  }
                }

                populatedFileIdRef.current = values.fileId;
                setIsLoadingColumnMetadata(false);
                setCurrentLoadingFileId('');
                return;
              }

              if (signal.aborted) return;

              // Use initialData if editing that specific file
              if (isEditMode && initialData?.SelectedTables === values.fileId) {
                const initialColumns = parseColumnMetadata(initialData);
                if (initialColumns.length > 0) {
                  setFieldValue('columnMetadata', initialColumns, false);

                  // Populate source instructions from initialData
                  const sourceInstructions = parseSourceInstructions(initialData);
                  if (sourceInstructions) {
                    setFieldValue('sourceInstructions', sourceInstructions, false);
                  }

                  populatedFileIdRef.current = values.fileId;
                  setIsLoadingColumnMetadata(false);
                  setCurrentLoadingFileId('');
                  return;
                }
              }

              if (signal.aborted) return;

              // Generate fresh schema for current file
              if (selectedTable?.schema_definition) {
                try {
                  const schema = JSON.parse(selectedTable.schema_definition);
                  const cols = schema.columns;
                  const metadata: ColumnMetadata[] = cols.map((col: any) => ({
                    columnName: col.name,
                    description: '',
                    synonymsNotes: '',
                    sourceInstructions: '',
                  }));

                  if (signal.aborted) return;

                  setFieldValue('columnMetadata', metadata, false);

                  //  Clear source instructions when generating fresh schema
                  setFieldValue('sourceInstructions', '', false);

                  populatedFileIdRef.current = values.fileId;
                } catch (error) {
                  console.error('Schema parse error:', error);
                }
              }

              setIsLoadingColumnMetadata(false);
              setCurrentLoadingFileId('');
            } catch (error) {
              if (!signal.aborted) {
                console.error('Error loading column metadata:', error);
                setIsLoadingColumnMetadata(false);
                setCurrentLoadingFileId('');
              }
            }
          };

          loadColumnMetadata();

          // Cleanup function
          return () => {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
          };
        }, [
          values.fileId,
          values.sourceType,
          selectedTable?.schema_definition,
          isEditMode,
          initialData?.SelectedTables,
          getColumnDescriptionsFromExistingSources,
          parseColumnMetadata,
          setFieldValue,
        ]);

        // Update toggle status - memoize to prevent unnecessary updates
        useEffect(() => {
          if (values.sourceType === 'Lakehouse' && values.fileId) {
            const existingTable = existingLakehouseTableIds.find((item: any) => item.tableId === values.fileId);
            if (existingTable && isSourceActive !== existingTable.isActive) {
              setIsSourceActive(existingTable.isActive);
            }
          }
        }, [values.fileId, values.sourceType, existingLakehouseTableIds]);

        // Extract source instructions mapping for each table
        const existingSourceInstructions = useMemo(() => {
          if (!dataAgentSourcesResponse?.data_agent_sources) {
            return new Map<string, string>();
          }
          const map = new Map<string, string>();
          dataAgentSourcesResponse.data_agent_sources
            .filter((source: any) => source.SourceType === 'Lakehouse')
            .forEach((source: any) => {
              const tableId = source.SelectedTables || source.DataTableId || source.data_table_id;
              if (tableId) {
                const instructions = parseSourceInstructions(source);
                map.set(tableId.toUpperCase(), instructions || '');
              }
            });
          return map;
        }, [dataAgentSourcesResponse, parseSourceInstructions]);

        // Update source instructions based on selected file - similar to toggle status
        useEffect(() => {
          if (values.sourceType === 'Lakehouse' && values.fileId) {
            const existingInstructions = existingSourceInstructions.get(values.fileId.toUpperCase());

            // Only update if we have instructions and they're different from current value
            if (existingInstructions !== undefined && existingInstructions !== values.sourceInstructions) {
              setFieldValue('sourceInstructions', existingInstructions, false);
            }
          } else if (values.sourceType === 'Lakehouse' && !values.fileId && values.sourceInstructions) {
            // Clear source instructions when no file is selected
            setFieldValue('sourceInstructions', '', false);
          }
        }, [values.fileId, values.sourceType, existingSourceInstructions, setFieldValue]);

        // CRITICAL: Memoize callbacks to prevent re-creating on every render
        const handleUpdateDescription = useCallback(
          (index: number, value: string) => {
            setFieldValue(`columnMetadata[${index}].description`, value, false);
          },
          [setFieldValue],
        );

        const handleUpdateSynonyms = useCallback(
          (index: number, value: string) => {
            setFieldValue(`columnMetadata[${index}].synonymsNotes`, value, false);
          },
          [setFieldValue],
        );

        // How It Works Component - Shown when no source type selected
        const HowItWorksGuide = () => (
          <Box
            sx={{
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              border: '1px solid',
              borderColor: '#2196f3',
              bgcolor: '#e3f2fd',
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1565c0', mb: 3 }}>
              {t('dataAgents.source.howItWorks.title')}
            </Typography>
            <Box sx={{ maxWidth: 600, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <LooksOneIcon sx={{ fontSize: 28, color: '#1976d2', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {t('dataAgents.source.howItWorks.step1Title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dataAgents.source.howItWorks.step1Description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <LooksTwoIcon sx={{ fontSize: 28, color: '#1976d2', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {t('dataAgents.source.howItWorks.step2Title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dataAgents.source.howItWorks.step2Description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Looks3Icon sx={{ fontSize: 28, color: '#1976d2', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {t('dataAgents.source.howItWorks.step3Title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dataAgents.source.howItWorks.step3Description')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        );

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
              <Form>
                <Box sx={{ width: '100%' }}>
                  {/* Source Type with Toggle in Grid layout */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={values.sourceType === 'DataConnection' ? 12 : 6}>
                      {/* <Grid size={6}> */}
                      <HelpCallout
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('dataAgents.source.sourceType')}
                            <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                              *
                            </Box>
                          </span>
                        }
                        labelId="sourcetype-label"
                        fieldId="sourceType"
                        helpText={t('dataAgents.source.helpTexts.sourceType')}
                      />
                      <Autocomplete
                        options={sourceTypeOptions}
                        getOptionLabel={(option) => option.label}
                        getOptionDisabled={(option) => !!option.disabled}
                        value={sourceTypeOptions.find((st) => st.value === values.sourceType) || null}
                        onChange={(_, newValue) => {
                          const value = newValue?.value || '';
                          setFieldValue('sourceType', value);

                          if (value === 'DataConnection') {
                            setFieldValue('lakehouseId', '');
                            setSelectedLakehouseId('');
                            setFieldValue('fileId', '');
                            setFieldValue('columnMetadata', [], false);
                            populatedFileIdRef.current = ''; // Reset ref
                          } else if (value === 'Lakehouse') {
                            setFieldValue('dataConnectionId', '');
                          }
                        }}
                        onBlur={handleBlur}
                        disabled={isEditMode || isSubmitting}
                        renderOption={(props, option) => (
                          <Tooltip
                            title={
                              option.disabled
                                ? option.value === 'Lakehouse'
                                  ? t(
                                      'dataAgents.source.lakehouseDisabledHint',
                                      'A Data Connector is already configured',
                                    )
                                  : t(
                                      'dataAgents.source.dataConnectorDisabledHint',
                                      'A Lakehouse is already configured',
                                    )
                                : ''
                            }
                            placement="right"
                          >
                            <Box component="li" {...props} sx={{ opacity: option.disabled ? 0.5 : 1 }}>
                              {option.label}
                            </Box>
                          </Tooltip>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="sourceType"
                            size="small"
                            placeholder={t('dataAgents.source.selectSourceType')}
                            error={Boolean(touched.sourceType && errors.sourceType)}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                      {touched.sourceType && errors.sourceType && (
                        <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                          {getErrorMessage(errors.sourceType)}
                        </Typography>
                      )}
                    </Grid>
                    {values.sourceType !== 'DataConnection' && (
                      <Grid size={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 2 }}>
                          <Typography variant="body2" sx={{ mr: 2 }}>
                            {t('dataAgents.source.status')}
                          </Typography>
                          <Tooltip
                            title={
                              isSourceActive
                                ? t('dataAgents.source.disableSource')
                                : t('dataAgents.source.enableSource')
                            }
                          >
                            <Switch
                              checked={isSourceActive}
                              onChange={handleToggleStatus}
                              color="primary"
                              size="small"
                            />
                          </Tooltip>
                          <Typography variant="body2" color={isSourceActive ? 'success.main' : 'text.secondary'}>
                            {isSourceActive ? t('dataAgents.source.active') : t('dataAgents.source.inactive')}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Show "How It Works" guide when no source type is selected */}
                  {!values.sourceType && <HowItWorksGuide />}

                  {/* Rest of the form - only shown when source type is selected */}
                  {values.sourceType && (
                    <>
                      {/* DataConnection Dropdown */}
                      {values.sourceType === 'DataConnection' && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={12}>
                            <HelpCallout
                              label={
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {t('dataAgents.source.dataConnection')}
                                  <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                    *
                                  </Box>
                                </span>
                              }
                              labelId="dataconnection-label"
                              fieldId="dataConnectionId"
                              helpText={t('dataAgents.source.helpTexts.dataConnection')}
                            />
                            <Autocomplete
                              options={dataConnections}
                              getOptionLabel={(option: any) => option.name || ''}
                              value={
                                dataConnections.find((dc: any) => dc.data_connection_id === values.dataConnectionId) ||
                                null
                              }
                              onChange={(_, newValue: any) => {
                                setFieldValue('dataConnectionId', newValue?.data_connection_id || '');
                              }}
                              onBlur={handleBlur}
                              disabled={isLoadingDataConnections || isEditMode || isConnectionLoading || isSubmitting}
                              loading={isLoadingDataConnections || isConnectionLoading || isLoadingDataAgentSources}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="dataConnectionId"
                                  size="small"
                                  placeholder={t('dataAgents.source.selectDataConnection')}
                                  error={Boolean(touched.dataConnectionId && errors.dataConnectionId)}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {(isLoadingDataConnections ||
                                          isConnectionLoading ||
                                          isLoadingDataAgentSources) && <CircularProgress size={20} />}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option.data_connection_id === value.data_connection_id
                              }
                              noOptionsText={
                                isErrorDataConnections
                                  ? t('dataAgents.source.errorLoadingDataConnections')
                                  : t('dataAgents.source.noDataConnectionsAvailable')
                              }
                            />
                            {touched.dataConnectionId && errors.dataConnectionId && (
                              <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                                {getErrorMessage(errors.dataConnectionId)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {/* Source Instructions for DataConnection */}
                      {values.sourceType === 'DataConnection' && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={12}>
                            <HelpCallout
                              label={t('dataAgents.source.sourceInstructionsOptional')}
                              labelId="sourceinstructions-dc-label"
                              fieldId="sourceInstructions"
                              helpText={t('dataAgents.source.helpTexts.sourceInstructionsDataConnection')}
                            />
                            <Field
                              as={TextField}
                              fullWidth
                              multiline
                              rows={4}
                              variant="outlined"
                              name="sourceInstructions"
                              size="small"
                              placeholder={t('dataAgents.source.sourceInstructionsPlaceholderDataConnection')}
                              disabled={isSubmitting}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {/* Lakehouse Dropdown */}
                      {values.sourceType === 'Lakehouse' && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={12}>
                            <HelpCallout
                              label={
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {t('dataAgents.source.lakehouseName')}
                                  <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                    *
                                  </Box>
                                </span>
                              }
                              labelId="lakehouse-label"
                              fieldId="lakehouseId"
                              helpText={t('dataAgents.source.helpTexts.lakehouse')}
                            />
                            <Autocomplete
                              options={lakehouses}
                              // getOptionLabel={(option: any) => option.name || ''}
                              getOptionLabel={(option: any) => {
                                if (!option || !option.name) return '';
                                // Build label: {Name}, {Code}, {Description} (if present)
                                let label = `${option.name}`;

                                if (option.code) {
                                  label += `, ${option.code}`;
                                }

                                if (option.description && option.description.trim() !== '') {
                                  label += `, ${option.description}`;
                                }

                                return label;
                              }}
                              value={lakehouses.find((lh: any) => lh.lakehouse_id === values.lakehouseId) || null}
                              onChange={(_, newValue: any) => {
                                const value = newValue?.lakehouse_id || '';
                                setFieldValue('lakehouseId', value);
                                setSelectedLakehouseId(value);
                                if (!isEditMode) {
                                  setFieldValue('fileId', '');
                                  setFieldValue('columnMetadata', [], false);
                                  populatedFileIdRef.current = ''; // Reset ref
                                }
                              }}
                              onBlur={handleBlur}
                              disabled={isLoadingLakehouses || isEditMode || isSubmitting}
                              loading={isLoadingLakehouses}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="lakehouseId"
                                  size="small"
                                  placeholder={t('dataAgents.source.selectLakehouse')}
                                  error={Boolean(touched.lakehouseId && errors.lakehouseId)}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isLoadingLakehouses && <CircularProgress size={20} />}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option.lakehouse_id === value.lakehouse_id
                              }
                              noOptionsText={
                                isErrorLakehouses
                                  ? t('dataAgents.source.errorLoadingLakehouses')
                                  : t('dataAgents.source.noLakehousesAvailable')
                              }
                            />
                            {touched.lakehouseId && errors.lakehouseId && (
                              <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                                {getErrorMessage(errors.lakehouseId)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {/* Files Dropdown */}
                      {values.lakehouseId && values.sourceType === 'Lakehouse' && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={12}>
                            <HelpCallout
                              label={
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {t('dataAgents.source.fileName')}
                                  <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                    *
                                  </Box>
                                </span>
                              }
                              labelId="filename-label"
                              fieldId="fileId"
                              helpText={t('dataAgents.source.helpTexts.fileName')}
                            />
                            <Autocomplete
                              options={tables}
                              getOptionLabel={(option: any) => option.name || ''}
                              value={tables.find((t: any) => t.data_table_id === values.fileId) || null}
                              onChange={(_, newValue: any, reason) => {
                                const value = newValue?.data_table_id || '';

                                // Cancel previous loading operation
                                if (abortControllerRef.current) {
                                  abortControllerRef.current.abort();
                                }

                                // Clear the input text to prevent it from getting stuck
                                if (reason === 'selectOption' || reason === 'clear') {
                                  setFieldValue('fileId', value);
                                  setCheckDataTable(value);

                                  // Clear source instructions when clearing file selection
                                  if (!value) {
                                    setFieldValue('sourceInstructions', '', false);
                                  }
                                  populatedFileIdRef.current = '';

                                  if (value) {
                                    setIsLoadingColumnMetadata(true);
                                  }
                                }
                              }}
                              onBlur={handleBlur}
                              disabled={isLoadingFiles || !goldLayerId || isSubmitting}
                              loading={isLoadingFiles}
                              disableClearable={false}
                              readOnly={false}
                              freeSolo={false}
                              disablePortal={false}
                              clearOnBlur={true}
                              selectOnFocus={true}
                              openOnFocus={true}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="fileId"
                                  size="small"
                                  placeholder={t('dataAgents.source.selectFile')}
                                  error={Boolean(touched.fileId && errors.fileId)}
                                  InputProps={{
                                    ...params.InputProps,
                                    readOnly: false,
                                    endAdornment: (
                                      <>
                                        {isLoadingFiles && <CircularProgress size={20} />}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option.data_table_id === value.data_table_id
                              }
                              noOptionsText={
                                !goldLayerId
                                  ? t('dataAgents.source.noGoldLayerFound')
                                  : isErrorFiles
                                    ? t('dataAgents.source.errorLoadingFiles')
                                    : t('dataAgents.source.noFilesAvailable')
                              }
                            />
                            {touched.fileId && errors.fileId && (
                              <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                                {getErrorMessage(errors.fileId)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {/* Column Metadata Grid */}
                      {values.sourceType === 'Lakehouse' && values.fileId && (
                        <Box sx={{ mb: 3 }}>
                          <HelpCallout
                            label={t('dataAgents.source.columnDescriptionsAndSynonyms')}
                            labelId="columnmetadata-label"
                            fieldId="columnMetadata"
                            helpText={t('dataAgents.source.helpTexts.columnMetadata')}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('dataAgents.source.helpsAIUnderstand')}
                          </Typography>

                          {/* FIXED: Check loading state FIRST before checking array length */}
                          {isLoadingColumnMetadata ? (
                            // Show loading state when metadata is being loaded
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                gap: 2,
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                              }}
                            >
                              <CircularProgress size={40} />
                              <Typography variant="body2" color="text.secondary">
                                {t('dataAgents.source.loadingColumnMetadata')}
                              </Typography>
                            </Box>
                          ) : values.columnMetadata.length > 0 ? (
                            <>
                              {/* Search Bar with Column Count */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mb: 2,
                                  p: 2,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Search columns..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  InputProps={{
                                    startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }} />,
                                  }}
                                  sx={{ flex: 1 }}
                                />
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    whiteSpace: 'nowrap',
                                    px: 2,
                                    py: 1,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    borderRadius: 1,
                                    minWidth: '150px',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {configuredCount}/{values.columnMetadata.length}
                                  </Typography>
                                  <Typography variant="caption">columns configured</Typography>
                                </Box>
                              </Box>

                              <Paper elevation={1} sx={{ overflow: 'hidden', width: '100%' }}>
                                <TableContainer sx={{ maxHeight: 700, width: '100%' }}>
                                  <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            width: '140px',
                                            borderBottom: '2px solid',
                                            borderColor: 'grey.300',
                                            maxWidth: '140px',
                                          }}
                                        >
                                          {t('dataAgents.source.columnName')}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            width: 'calc(60% - 70px)',
                                            borderBottom: '2px solid',
                                            borderColor: 'grey.300',
                                          }}
                                        >
                                          {t('dataAgents.source.description')}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            width: 'calc(40% - 70px)',
                                            borderBottom: '2px solid',
                                            borderColor: 'grey.300',
                                          }}
                                        >
                                          {t('dataAgents.source.synonymsNotes')}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      <FieldArray name="columnMetadata">
                                        {() =>
                                          filteredColumnMetadata.map((column: ColumnMetadata) => {
                                            const originalIndex = values.columnMetadata.findIndex(
                                              (col: ColumnMetadata) => col.columnName === column.columnName,
                                            );
                                            return (
                                              <ColumnRow
                                                key={column.columnName}
                                                column={column}
                                                originalIndex={originalIndex}
                                                onUpdateDescription={handleUpdateDescription}
                                                onUpdateSynonyms={handleUpdateSynonyms}
                                                descriptionPlaceholder={t('dataAgents.source.descriptionPlaceholder')}
                                                synonymsPlaceholder={t('dataAgents.source.synonymsNotesPlaceholder')}
                                              />
                                            );
                                          })
                                        }
                                      </FieldArray>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Paper>
                            </>
                          ) : (
                            // Only show no columns message when NOT loading and array is empty
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {t('dataAgents.source.noColumnsAvailable')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Form>
            </Box>

            {/* Sticky Action Buttons at Bottom */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                pb: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Button variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }} disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const form = document.querySelector('form');
                  if (form) {
                    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }}
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                {isSubmitting ? <CircularProgress size={20} /> : isEditMode ? t('common.save') : t('common.create')}
              </Button>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};

export default AddEditDataAgentSourceUIForm;
